import {
  simpleRemoveNotification,
  simpleRestoreNotification,
} from 'hooks/notificationHandler'
import { useCallback, useState } from 'react'
import {
  useRemoveWorkflowRecordFromRecordsMutation,
  useRestoreWorkflowRecordFromRecordsMutation,
} from 'schema'
import { DefaultMutationHookOptions } from 'utils/defaults'
import type { RecordsTableProps } from '../components/RecordsTable/types'

type LifecycleOperation = 'Remove' | 'Restore'

function lifecycleMutationDisabled(operation: LifecycleOperation | undefined) {
  return operation ? { message: `${operation} is already in progress.` } : false
}

type UseWorkflowRecordLifecycleOptions = {
  canRemoveRestore: boolean
  onReload: () => unknown
}

/**
 * Resolves the Records page's permission-aware remove and restore actions.
 *
 * Successful mutations notify with the server-resolved record name and reload
 * the records query so lifecycle state stays authoritative on the server.
 */
export function useWorkflowRecordLifecycle({
  canRemoveRestore,
  onReload,
}: UseWorkflowRecordLifecycleOptions): RecordsTableProps['actions'] {
  const [pendingOperation, setPendingOperation] = useState<LifecycleOperation>()
  const [remove, { loading: removing }] =
    useRemoveWorkflowRecordFromRecordsMutation({
      ...DefaultMutationHookOptions,
      onCompleted: ({ removeWorkflowRecord: { removed } }) =>
        simpleRemoveNotification(removed.recordName),
    })
  const [restore, { loading: restoring }] =
    useRestoreWorkflowRecordFromRecordsMutation({
      ...DefaultMutationHookOptions,
      onCompleted: ({ restoreWorkflowRecord: { restored } }) =>
        simpleRestoreNotification(restored.recordName),
    })
  let operation = pendingOperation
  if (!operation && removing) operation = 'Remove'
  if (!operation && restoring) operation = 'Restore'
  const disabled = lifecycleMutationDisabled(operation)

  return useCallback(
    (record) => {
      if (!canRemoveRestore) return {}

      return {
        lifecycle: {
          active: !record.deleted,
          remove: {
            disabled,
            onClick: async () => {
              setPendingOperation('Remove')
              try {
                const result = await remove({
                  variables: { input: { id: record.id } },
                })
                if (result.data) await onReload()
              } finally {
                setPendingOperation(undefined)
              }
            },
            permission: true,
          },
          restore: {
            disabled,
            onClick: async () => {
              setPendingOperation('Restore')
              try {
                const result = await restore({
                  variables: { input: { id: record.id } },
                })
                if (result.data) await onReload()
              } finally {
                setPendingOperation(undefined)
              }
            },
            permission: true,
          },
        },
      }
    },
    [canRemoveRestore, disabled, onReload, remove, restore]
  )
}
